import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { FiClock, FiChevronDown, FiSearch, FiX } from 'react-icons/fi';
import { useColorContext } from '../context/colorContext';
import { useUserContext } from '../context/userContext';
import { useLanguageContext } from '../context/languageContext';

const ANIM_STYLE_ID = 'plynote-my-notes-menu-anim';
if (typeof document !== 'undefined') {
	const existingStyleEl = document.getElementById(ANIM_STYLE_ID);
	const baseKeyframes = `
		@keyframes myNotesMenuIn { from { opacity: 0; transform: scale(.95) translateY(-12px); } to { opacity: 1; transform: scale(1) translateY(0); } }
		@keyframes myNotesMenuOut { from { opacity: 1; transform: scale(1) translateY(0); } to { opacity: 0; transform: scale(.95) translateY(-8px); } }
		@keyframes myNotesBackdropIn { from { opacity: 0; } to { opacity: 1; } }
		@keyframes myNotesBackdropOut { from { opacity: 1; } to { opacity: 0; } }
		@keyframes myNotesSoftGlow { 0%, 100% { opacity: .75; } 50% { opacity: 1; } }
	`;
	const spinnerKeyframes = `
		@keyframes myNotesSpinnerSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
	`;

	if (!existingStyleEl) {
		const styleEl = document.createElement('style');
		styleEl.id = ANIM_STYLE_ID;
		styleEl.textContent = `${baseKeyframes}${spinnerKeyframes}`;
		document.head.appendChild(styleEl);
	} else if (!String(existingStyleEl.textContent || '').includes('@keyframes myNotesSpinnerSpin')) {
		existingStyleEl.textContent = `${existingStyleEl.textContent || ''}\n${spinnerKeyframes}`;
	}
}

const BACKEND_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

const LITERAL_DATE_FORMATTER = new Intl.DateTimeFormat('en-US', {
	day: 'numeric',
	month: 'long',
	year: 'numeric',
});

const TIME_FORMATTER = new Intl.DateTimeFormat('en-US', {
	hour: '2-digit',
	minute: '2-digit',
});

const getNoteDate = (note) => {
	const value = note?.updatedAt || note?.createdAt || null;
	if (!value) return null;
	const parsed = new Date(value);
	return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const isSameCalendarDay = (left, right) => (
	left.getFullYear() === right.getFullYear() &&
	left.getMonth() === right.getMonth() &&
	left.getDate() === right.getDate()
);

// Note: date formatting that depends on translations is defined inside the component

const getSortTime = (note) => {
	const stamp = note?.updatedAt || note?.createdAt || null;
	const time = stamp ? new Date(stamp).getTime() : 0;
	return Number.isFinite(time) ? time : 0;
};

function MyNotes() {
	const navigate = useNavigate();
	const { textPrimary, textSecondary, activeAccent, bgPrimary, bgSecondary } = useColorContext();
	const { user_id, is_authenticated, access_token } = useUserContext();
	const { t } = useLanguageContext();

	const formatRelativeNoteDate = (value) => {
		if (!value) return t('notes.date.unknown');
		const parsed = new Date(value);
		if (Number.isNaN(parsed.getTime())) return t('notes.date.unknown');

		const now = new Date();
		const diffMs = now.getTime() - parsed.getTime();
		const lessThan24Hours = diffMs >= 0 && diffMs < 24 * 60 * 60 * 1000;

		if (lessThan24Hours) {
			if (isSameCalendarDay(parsed, now)) {
				return t('notes.date.today', { time: TIME_FORMATTER.format(parsed) });
			}

			const yesterday = new Date(now);
			yesterday.setDate(now.getDate() - 1);
			if (isSameCalendarDay(parsed, yesterday)) {
				return t('notes.date.yesterday', { time: TIME_FORMATTER.format(parsed) });
			}

			return t('notes.date.today', { time: TIME_FORMATTER.format(parsed) });
		}

		return LITERAL_DATE_FORMATTER.format(parsed);
	};

	const [notes, setNotes] = useState([]);
	const [isLoading, setIsLoading] = useState(true);
	const [errorMessage, setErrorMessage] = useState('');
	const [hoveredNoteId, setHoveredNoteId] = useState('');
	const [searchTerm, setSearchTerm] = useState('');
	const [sortOrder, setSortOrder] = useState('desc');
	const [isSortPanelOpen, setIsSortPanelOpen] = useState(false);
	const [openMenuNoteId, setOpenMenuNoteId] = useState('');
	const [menuRenameDraft, setMenuRenameDraft] = useState('');
	const [actionLoadingNoteId, setActionLoadingNoteId] = useState('');
	const [menuStatusMessage, setMenuStatusMessage] = useState('');
	const [menuIsClosing, setMenuIsClosing] = useState(false);
	const [loadRetryCount, setLoadRetryCount] = useState(0);
	const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
	const [windowHeight, setWindowHeight] = useState(typeof window !== 'undefined' ? window.innerHeight : 900);

	useEffect(() => {
		let isCancelled = false;

		const loadNotes = async () => {
			setIsLoading(true);
			setErrorMessage('');

			const accessToken = String(access_token || '').trim();
			if (!accessToken) {
				if (!isCancelled) {
					setNotes([]);
					setErrorMessage('Invalid session. Sign in to view your notes.');
					setIsLoading(false);
				}
				return;
			}

			try {
				const response = await fetch(`${BACKEND_BASE_URL}/api/note-content`, {
					method: 'GET',
					cache: 'no-store',
					headers: {
						Authorization: `Bearer ${accessToken}`,
						'Cache-Control': 'no-cache, no-store, max-age=0',
						Pragma: 'no-cache',
					},
				});

				const payload = await response.json().catch(() => ({}));
				if (!response.ok || !payload?.success) {
					throw new Error(String(payload?.error || 'Unable to load your notes.'));
				}

				const seenIds = new Set();
				const normalized = Array.isArray(payload?.data)
					? payload.data.filter((note) => {
						const id = String(note?.noteId || '').trim();
						if (!id || seenIds.has(id)) return false;
						seenIds.add(id);
						return true;
					})
					: [];
				normalized.sort((a, b) => getSortTime(b) - getSortTime(a));

				if (!isCancelled) {
					setNotes(normalized);
					if (loadRetryCount !== 0) setLoadRetryCount(0);
				}
			} catch (error) {
				if (!isCancelled) {
					const isNetworkFailure = String(error?.message || '').toLowerCase().includes('failed to fetch');
					const shouldRetry = isNetworkFailure && loadRetryCount < 1;

					if (shouldRetry) {
						setLoadRetryCount((current) => current + 1);
						window.setTimeout(() => {
							loadNotes();
						}, 700);
						return;
					}

					setNotes([]);
					setErrorMessage(isNetworkFailure
						? 'The server is temporarily unavailable. Refresh in a few seconds.'
						: (error?.message || 'Unable to load your notes.'));
				}
			} finally {
				if (!isCancelled) {
					setIsLoading(false);
				}
			}
		};

		loadNotes();

		return () => {
			isCancelled = true;
		};
	}, [access_token, user_id, loadRetryCount]);

	useEffect(() => {
		const handleResize = () => {
			setWindowWidth(window.innerWidth);
			setWindowHeight(window.innerHeight);
		};
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	const isMobile = windowWidth <= 640;
	const isTiny = windowWidth <= 420;
	const isNarrow = windowWidth <= 920;
	const isShort = windowHeight <= 760;

	const emptyMessage = useMemo(() => {
		if (!is_authenticated || !user_id) {
			return t('notes.empty.signIn');
		}
		return t('notes.empty.noNotes');
	}, [is_authenticated, user_id, t]);

	const sortedNotes = useMemo(() => {
		const next = Array.isArray(notes) ? [...notes] : [];
		next.sort((a, b) => {
			const left = getSortTime(a);
			const right = getSortTime(b);
			return sortOrder === 'asc' ? left - right : right - left;
		});
		return next;
	}, [notes, sortOrder]);

	const latestUpdatedAt = useMemo(() => {
		if (sortedNotes.length === 0) return null;
		const [first] = sortedNotes;
		return first?.updatedAt || first?.createdAt || null;
	}, [sortedNotes]);

	const filteredNotes = useMemo(() => {
		const query = String(searchTerm || '').trim().toLowerCase();
		if (!query) return sortedNotes;

		return sortedNotes.filter((note) => {
			const name = String(note?.nameNote || '').toLowerCase();
			return name.includes(query);
		});
	}, [sortedNotes, searchTerm]);

	const groupedFilteredNotes = useMemo(() => {
		const now = new Date();
		const thirtyDaysAgo = new Date(now);
		thirtyDaysAgo.setDate(now.getDate() - 30);

		const grouped = {
			today: [],
			last30Days: [],
			earlier: [],
		};

		filteredNotes.forEach((note) => {
			const noteDate = getNoteDate(note);
			if (!noteDate) {
				grouped.earlier.push(note);
				return;
			}

			if (isSameCalendarDay(noteDate, now)) {
				grouped.today.push(note);
				return;
			}

			if (noteDate.getTime() >= thirtyDaysAgo.getTime()) {
				grouped.last30Days.push(note);
				return;
			}

			grouped.earlier.push(note);
		});

		return grouped;
	}, [filteredNotes]);

	const selectedMenuNote = useMemo(() => {
		if (!openMenuNoteId) return null;
		return notes.find((note) => String(note?.noteId || '').trim() === openMenuNoteId) || null;
	}, [notes, openMenuNoteId]);

	const closeMenu = () => {
		setOpenMenuNoteId('');
		setMenuRenameDraft('');
		setMenuStatusMessage('');
		setMenuIsClosing(false);
	};

	const closeMenuAnimated = () => {
		if (!openMenuNoteId || menuIsClosing) return;
		setMenuIsClosing(true);
		window.setTimeout(() => {
			closeMenu();
		}, 190);
	};

	const hexToRgba = (hex, alpha) => {
		const safeHex = /^#([A-Fa-f0-9]{6})$/.test(hex || '') ? hex : '#FFFFFF';
		const r = parseInt(safeHex.slice(1, 3), 16);
		const g = parseInt(safeHex.slice(3, 5), 16);
		const b = parseInt(safeHex.slice(5, 7), 16);
		return `rgba(${r}, ${g}, ${b}, ${alpha})`;
	};

	const upsertUpdatedNote = (updatedNote) => {
		setNotes((currentNotes) => {
			const next = currentNotes.map((note) => {
				if (String(note?.noteId || '') !== String(updatedNote?.noteId || '')) return note;
				return {
					...note,
					nameNote: updatedNote?.nameNote ?? note.nameNote,
					updatedAt: updatedNote?.updatedAt ?? note.updatedAt,
				};
			});
			next.sort((a, b) => getSortTime(b) - getSortTime(a));
			return next;
		});
	};

	const handleRenameNote = async (noteId, nextNameRaw) => {
		const accessToken = String(access_token || '').trim();
		if (!accessToken || !noteId) {
			setErrorMessage('Invalid session. Sign in to edit a note.');
			return;
		}

		const sanitizedName = String(nextNameRaw || '').trim();
		if (!sanitizedName) {
			setErrorMessage('Note name cannot be empty.');
			setMenuStatusMessage('Note name cannot be empty.');
			return;
		}

		setActionLoadingNoteId(noteId);
		setErrorMessage('');
		setMenuStatusMessage('Renaming...');

		try {
			const response = await fetch(`${BACKEND_BASE_URL}/api/note-content/${encodeURIComponent(noteId)}/name`, {
				method: 'PATCH',
				headers: {
					Authorization: `Bearer ${accessToken}`,
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({ nameNote: sanitizedName }),
			});

			const payload = await response.json().catch(() => ({}));
			if (!response.ok || !payload?.success) {
				throw new Error(String(payload?.error || 'Unable to rename note.'));
			}

			upsertUpdatedNote(payload?.data || { noteId, nameNote: sanitizedName });
			closeMenuAnimated();
		} catch (error) {
			setErrorMessage(error?.message || 'Unable to rename note.');
			setMenuStatusMessage(error?.message || 'Unable to rename note.');
		} finally {
			setActionLoadingNoteId('');
		}
	};

	const handleDeleteNote = async (noteId) => {
		const accessToken = String(access_token || '').trim();
		if (!accessToken || !noteId) {
			setErrorMessage('Invalid session. Sign in to delete a note.');
			return;
		}

		const confirmed = window.confirm(t('notes.menu.deleteConfirm'));
		if (!confirmed) {
			return;
		}

		setActionLoadingNoteId(noteId);
		setErrorMessage('');
		setMenuStatusMessage('Deleting...');

		try {
			const response = await fetch(`${BACKEND_BASE_URL}/api/note-content/${encodeURIComponent(noteId)}`, {
				method: 'DELETE',
				headers: {
					Authorization: `Bearer ${accessToken}`,
				},
			});

			const payload = await response.json().catch(() => ({}));
			if (!response.ok || !payload?.success) {
				throw new Error(String(payload?.error || 'Unable to delete note.'));
			}

			setNotes((currentNotes) => currentNotes.filter((note) => String(note?.noteId || '') !== String(noteId)));
			closeMenuAnimated();
		} catch (error) {
			setErrorMessage(error?.message || 'Unable to delete note.');
			setMenuStatusMessage(error?.message || 'Unable to delete note.');
		} finally {
			setActionLoadingNoteId('');
		}
	};

	const renderNoteCard = (note) => {
		const noteId = String(note?.noteId || '').trim();
		const noteName = String(note?.nameNote || '').trim() || t('notes.untitled');
		const updatedAt = note?.updatedAt || note?.createdAt;
		const isHovered = hoveredNoteId && hoveredNoteId === noteId;

		return (
			<div
				key={noteId || `${noteName}-${updatedAt || 'unknown'}`}
				role="button"
				tabIndex={0}
				onMouseEnter={() => setHoveredNoteId(noteId)}
				onMouseLeave={() => setHoveredNoteId('')}
				onKeyDown={(event) => {
					if (!noteId) return;
					if (event.key === 'Enter' || event.key === ' ') {
						event.preventDefault();
						navigate(`/note-editor/${encodeURIComponent(noteId)}`);
					}
				}}
				onClick={(event) => {
					if (event.target.closest('[data-note-menu-button]') || event.target.closest('[data-note-menu-panel]')) {
						return;
					}
					if (!noteId) return;
					navigate(`/note-editor/${encodeURIComponent(noteId)}`);
				}}
				style={{
					position: 'relative',
					overflow: 'hidden',
					textAlign: 'left',
					padding: isTiny ? '9px 10px' : isMobile ? '11px 13px' : isNarrow ? '15px 16px' : '18px 20px',
					borderRadius: isMobile ? 12 : 16,
					border: 'none',
					backgroundColor: isHovered ? hexToRgba(textPrimary, 0.14) : hexToRgba(textPrimary, 0.1),
					cursor: noteId ? 'pointer' : 'default',
					color: textPrimary,
					transform: isHovered ? 'translateY(-2px)' : 'translateY(0)',
					boxShadow: isHovered
						? `0 7px 14px ${hexToRgba(textPrimary, 0.07)}, inset 0 1px 0 ${hexToRgba(textPrimary, 0.1)}`
						: `0 2px 0 ${hexToRgba(textPrimary, 0.06)}`,
					transition: 'background-color 180ms ease, box-shadow 180ms ease, transform 180ms ease',
				}}
			>
				<button
					type="button"
					data-note-menu-button="true"
					onPointerDown={(event) => {
						event.preventDefault();
						event.stopPropagation();
					}}
					onClick={(event) => {
						event.stopPropagation();
						setOpenMenuNoteId((current) => {
							if (current === noteId) {
								setMenuRenameDraft('');
								setMenuIsClosing(true);
								window.setTimeout(() => {
									closeMenu();
								}, 190);
								return '';
							}
							setMenuRenameDraft(noteName);
							setMenuStatusMessage('');
							setMenuIsClosing(false);
							return noteId;
						});
					}}
					style={{
						position: 'absolute',
						right: isMobile ? 8 : 14,
						top: '50%',
						transform: 'translateY(-50%)',
						width: isMobile ? 28 : 36,
						height: isMobile ? 28 : 36,
						border: 'none',
						backgroundColor: 'transparent',
						color: textSecondary,
						cursor: 'pointer',
						display: 'inline-flex',
						alignItems: 'center',
						justifyContent: 'center',
						fontSize: isTiny ? 18 : isMobile ? 20 : 26,
						lineHeight: 1,
						padding: 0,
						borderRadius: 999,
						zIndex: 3,
						transition: 'background-color 160ms ease, color 160ms ease, transform 160ms ease',
					}}
					title={t('notes.menu.actions')}
					onMouseEnter={(event) => {
						event.currentTarget.style.backgroundColor = hexToRgba(textPrimary, 0.06);
						event.currentTarget.style.color = textPrimary;
					}}
					onMouseLeave={(event) => {
						event.currentTarget.style.backgroundColor = 'transparent';
						event.currentTarget.style.color = textSecondary;
					}}
				>
					<span style={{ display: 'inline-block', transform: 'translateY(-1px)' }}>⋮</span>
				</button>

				<div style={{ position: 'relative', zIndex: 1, fontSize: isTiny ? 13 : isMobile ? 15 : isNarrow ? 18 : 22, fontWeight: 680, letterSpacing: '-0.01em', lineHeight: 1.2, paddingRight: isMobile ? 32 : 44 }}>{noteName}</div>
				<div style={{ marginTop: isMobile ? 4 : 8, fontSize: isTiny ? 10 : isMobile ? 11 : 13, color: hexToRgba(textPrimary, 0.74), fontWeight: 500 }}>
					{formatRelativeNoteDate(updatedAt)}
				</div>
			</div>
		);
	};

	return (
		<div
			style={{
				minHeight: '100%',
				overflow: 'auto',
				padding: isTiny ? '12px' : isMobile ? '14px' : 'clamp(14px, 3.8vw, 48px)',
				backgroundColor: bgPrimary,
				backgroundImage: `
					linear-gradient(180deg, ${hexToRgba(bgSecondary, 0.92)} 0%, ${hexToRgba(bgPrimary, 1)} 100%),
					radial-gradient(circle at 100% 0%, ${hexToRgba(activeAccent, 0.16)} 0%, transparent 36%),
					radial-gradient(circle at 8% 0%, ${hexToRgba(activeAccent, 0.08)} 0%, transparent 28%)
				`,
				fontFamily: 'Inter, "SF Pro Text", "SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
			}}
		>
				<div style={{ width: '100%', maxWidth: isTiny ? '100%' : isMobile ? '600px' : '860px', margin: '0 auto' }}>
				<div style={{ marginBottom: isTiny ? 16 : 24 }}>
					<h1 style={{ margin: isMobile ? '0 0 12px' : '0 0 18px', fontSize: isTiny ? 'clamp(16px, 6.5vw, 22px)' : isMobile ? 'clamp(18px, 5.5vw, 26px)' : 'clamp(20px, 3.8vw, 32px)', color: textPrimary, fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.08 }}>
						{t('nav.myNotes')}
					</h1>
					<div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: isTiny ? 8 : isMobile ? 10 : 16, marginTop: 10 }}>
						<div style={{ minWidth: 0, flex: '1 1 auto' }}>
							<div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, minHeight: isMobile ? 36 : 46, padding: isTiny ? '0 9px' : isMobile ? '0 10px' : '0 14px', borderRadius: 999, fontSize: isTiny ? 11 : isMobile ? 12 : 13, fontWeight: 600, color: textPrimary, backgroundColor: hexToRgba(activeAccent, 0.18), border: 'none' }}>
								{notes.length} {notes.length > 1 ? t('notes.pluralNotes') : t('notes.singleNote')}
							</div>
							{latestUpdatedAt ? (
								<div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, minHeight: isMobile ? 36 : 46, marginLeft: isMobile ? 8 : 12, padding: isTiny ? '0 9px' : isMobile ? '0 10px' : '0 14px', borderRadius: 12, fontSize: isTiny ? 10 : isMobile ? 11 : 13, color: textPrimary, backgroundColor: hexToRgba(textPrimary, 0.1), border: 'none' }}>
									<FiClock size={isTiny ? 11 : isMobile ? 12 : 14} />
									<span>{t('notes.lastUpdate')} {formatRelativeNoteDate(latestUpdatedAt)}</span>
								</div>
							) : null}
						</div>

						<div style={{ minWidth: isMobile ? 0 : 280, flex: isMobile ? '1 0 100%' : '0 1 420px', position: 'relative' }}>
							<button
								type="button"
								onClick={() => setIsSortPanelOpen((current) => !current)}
								aria-expanded={isSortPanelOpen}
								style={{ width: '100%', height: isTiny ? 36 : isMobile ? 38 : 46, minHeight: isTiny ? 36 : isMobile ? 38 : 46, padding: isTiny ? '0 10px' : '0 14px', borderRadius: 12, border: 'none', backgroundColor: hexToRgba(activeAccent, 0.16), color: textPrimary, fontSize: isTiny ? 11 : isMobile ? 12 : 13, fontWeight: 650, whiteSpace: 'nowrap', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, boxShadow: 'none', transition: 'background-color 180ms ease, transform 180ms ease' }}
								onMouseEnter={(event) => {
									event.currentTarget.style.backgroundColor = hexToRgba(activeAccent, 0.24);
									event.currentTarget.style.transform = 'translateY(-1px)';
								}}
								onMouseLeave={(event) => {
									event.currentTarget.style.backgroundColor = hexToRgba(activeAccent, 0.16);
									event.currentTarget.style.transform = 'translateY(0)';
								}}
							>
								<span>{t('notes.sort.label')} {sortOrder === 'desc' ? t('notes.sort.mostRecent') : t('notes.sort.oldest')}</span>
								<span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', transform: isSortPanelOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 180ms ease' }}>
									<FiChevronDown size={15} />
								</span>
							</button>

							<div style={{ position: 'absolute', top: 52, left: 0, right: 0, zIndex: 20, overflow: 'hidden', maxHeight: isSortPanelOpen ? 110 : 0, opacity: isSortPanelOpen ? 1 : 0, transform: isSortPanelOpen ? 'translateY(0)' : 'translateY(-6px)', transition: 'max-height 220ms ease, opacity 180ms ease, transform 220ms ease', borderRadius: 12, backgroundColor: hexToRgba(bgSecondary, 0.6), boxShadow: 'none', border: 'none', pointerEvents: isSortPanelOpen ? 'auto' : 'none' }}>
								<div style={{ display: 'grid', gap: 8, padding: isTiny ? 8 : 10 }}>
									<button type="button" onClick={() => { setSortOrder('asc'); setIsSortPanelOpen(false); }} style={{ height: isTiny ? 34 : 36, borderRadius: 10, border: 'none', backgroundColor: sortOrder === 'asc' ? hexToRgba(activeAccent, 0.24) : 'transparent', color: textPrimary, cursor: 'pointer', textAlign: 'left', padding: '0 12px', fontSize: isTiny ? 11 : 13, fontWeight: 600, transition: 'background-color 160ms ease, transform 160ms ease' }} onMouseEnter={(event) => { if (sortOrder !== 'asc') event.currentTarget.style.backgroundColor = hexToRgba(activeAccent, 0.12); event.currentTarget.style.transform = 'translateX(2px)'; }} onMouseLeave={(event) => { event.currentTarget.style.backgroundColor = sortOrder === 'asc' ? hexToRgba(activeAccent, 0.24) : 'transparent'; event.currentTarget.style.transform = 'translateX(0)'; }}>
										{t('notes.sort.oldestToNewest')}
									</button>
									<button type="button" onClick={() => { setSortOrder('desc'); setIsSortPanelOpen(false); }} style={{ height: isTiny ? 34 : 36, borderRadius: 10, border: 'none', backgroundColor: sortOrder === 'desc' ? hexToRgba(activeAccent, 0.24) : 'transparent', color: textPrimary, cursor: 'pointer', textAlign: 'left', padding: '0 12px', fontSize: isTiny ? 11 : 13, fontWeight: 600, transition: 'background-color 160ms ease, transform 160ms ease' }} onMouseEnter={(event) => { if (sortOrder !== 'desc') event.currentTarget.style.backgroundColor = hexToRgba(activeAccent, 0.12); event.currentTarget.style.transform = 'translateX(2px)'; }} onMouseLeave={(event) => { event.currentTarget.style.backgroundColor = sortOrder === 'desc' ? hexToRgba(activeAccent, 0.24) : 'transparent'; event.currentTarget.style.transform = 'translateX(0)'; }}>
										{t('notes.sort.newestToOldest')}
									</button>
								</div>
							</div>
						</div>
					</div>
				</div>

				<div
					style={{
						position: 'relative',
						display: 'flex',
						alignItems: 'center',
						marginBottom: isTiny ? 16 : 22,
					}}
				>
						<FiSearch
							size={isTiny ? 15 : 17}
							style={{
								position: 'absolute',
								left: isTiny ? 10 : 12,
								color: hexToRgba(textPrimary, 0.72),
								pointerEvents: 'none',
							}}
						/>
							<input
							type="text"
							value={searchTerm}
							onChange={(event) => setSearchTerm(event.target.value)}
								placeholder={t('notes.search.placeholder')}
							style={{
								width: '100%',
								height: isTiny ? 40 : 46,
								padding: isTiny ? '0 10px 0 34px' : '0 12px 0 38px',
								borderRadius: 12,
								border: 'none',
								backgroundColor: hexToRgba(bgPrimary, 0.78),
								color: textPrimary,
								fontSize: isTiny ? 12 : 14,
								outline: 'none',
								boxShadow: `inset 0 0 0 1px ${hexToRgba(textPrimary, 0.12)}`,
							}}
						/>
				</div>

				{isLoading ? (
					<div
						style={{
								height: isTiny ? 180 : 220,
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'center',
							justifyContent: 'center',
								gap: isTiny ? 10 : 14,
							borderRadius: 16,
							backgroundColor: hexToRgba(bgSecondary, 0.58),
							boxShadow: `inset 0 1px 0 ${hexToRgba(textPrimary, 0.06)}`,
						}}
					>
						<div
							aria-hidden="true"
							style={{
								width: isTiny ? 44 : 54,
								height: isTiny ? 44 : 54,
								borderRadius: '50%',
								animation: 'myNotesSpinnerSpin .95s linear infinite',
								boxShadow: 'none',
							}}
						>
							<svg width="54" height="54" viewBox="0 0 54 54" role="presentation">
								<circle
									cx="27"
									cy="27"
									r="21"
									fill="none"
									stroke={hexToRgba(textPrimary, 0.1)}
									strokeWidth="4"
									strokeLinecap="round"
									strokeDasharray="72 132"
								/>
							</svg>
						</div>
								<div style={{ color: hexToRgba(textPrimary, 0.82), fontSize: isTiny ? 12 : 14, fontWeight: 500 }}>
							{t('notes.loading')}
						</div>
					</div>
				) : null}

				{!isLoading && errorMessage ? (
					<div
						style={{
							padding: isTiny ? '12px 14px' : '14px 16px',
							borderRadius: 12,
							border: 'none',
							color: textPrimary,
							backgroundColor: hexToRgba(bgSecondary, 0.86),
							marginBottom: 16,
						}}
					>
						{errorMessage}
					</div>
				) : null}

				{!isLoading && !errorMessage && filteredNotes.length === 0 ? (
					<div
						style={{
							padding: isTiny ? '18px 16px' : '22px 20px',
							borderRadius: 14,
							border: 'none',
							color: hexToRgba(textPrimary, 0.72),
							backgroundColor: hexToRgba(bgSecondary, 0.65),
						}}
					>
						{searchTerm.trim() ? t('notes.empty.noMatch') : emptyMessage}
					</div>
				) : null}

				{!isLoading && !errorMessage && filteredNotes.length > 0 ? (
						<div style={{ display: 'grid', gap: isTiny ? 10 : isMobile ? 14 : 24 }}>
						{(sortOrder === 'asc'
							? [
								{ key: 'earlier', title: t('notes.sections.earlier'), notes: groupedFilteredNotes.earlier },
								{ key: 'last30days', title: t('notes.sections.last30days'), notes: groupedFilteredNotes.last30Days },
								{ key: 'today', title: t('notes.sections.today'), notes: groupedFilteredNotes.today },
							]
							: [
								{ key: 'today', title: t('notes.sections.today'), notes: groupedFilteredNotes.today },
								{ key: 'last30days', title: t('notes.sections.last30days'), notes: groupedFilteredNotes.last30Days },
								{ key: 'earlier', title: t('notes.sections.earlier'), notes: groupedFilteredNotes.earlier },
							])
							.filter((section) => section.notes.length > 0)
							.map((section) => (
								<div key={section.key} style={{ display: 'grid', gap: isTiny ? 6 : isMobile ? 8 : 12 }}>
									<div
										style={{
											fontSize: isTiny ? 10 : isMobile ? 11 : 12,
											fontWeight: 700,
											letterSpacing: '0.08em',
											textTransform: 'uppercase',
											color: hexToRgba(textPrimary, 0.72),
										}}
									>
										{section.title}
									</div>
									<div style={{ display: 'grid', gap: isTiny ? 6 : isMobile ? 8 : 12 }}>
										{section.notes.map((note) => renderNoteCard(note))}
									</div>
								</div>
							))}
					</div>
				) : null}

				{selectedMenuNote && typeof document !== 'undefined'
					? createPortal(
						<div
							data-note-menu-panel="true"
							onClick={(event) => {
								if (event.target === event.currentTarget) {
									closeMenuAnimated();
								}
							}}
							style={{
								position: 'fixed',
								inset: 0,
								zIndex: 9999,
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								padding: '20px',
								background: hexToRgba(bgPrimary, 0.78),
								backdropFilter: 'blur(7px)',
								pointerEvents: 'auto',
								animation: menuIsClosing ? 'myNotesBackdropOut .19s ease forwards' : 'myNotesBackdropIn .22s ease forwards',
							}}
						>
							<div
								onClick={(event) => event.stopPropagation()}
								onPointerDown={(event) => event.stopPropagation()}
								style={{
									width: isTiny ? 'min(560px, 96vw)' : 'min(760px, 96vw)',
									maxHeight: '88vh',
									overflowY: 'auto',
									borderRadius: 22,
										border: `1px solid ${hexToRgba(textPrimary, 0.14)}`,
										background: `
											linear-gradient(180deg, ${hexToRgba(bgPrimary, 0.985)} 0%, ${hexToRgba(bgSecondary, 0.965)} 100%),
											radial-gradient(circle at 100% 0%, ${hexToRgba(activeAccent, 0.12)} 0%, transparent 40%)
										`,
										boxShadow: `0 20px 46px ${hexToRgba(bgPrimary, 0.52)}, 0 0 0 1px ${hexToRgba(activeAccent, 0.16)}, inset 0 1px 0 ${hexToRgba(textPrimary, 0.05)}`,
									padding: isTiny ? 16 : isMobile ? 20 : 28,
									display: 'flex',
									flexDirection: 'column',
									gap: isTiny ? 14 : 20,
									animation: menuIsClosing ? 'myNotesMenuOut .19s cubic-bezier(.4,0,1,1) forwards' : 'myNotesMenuIn .26s cubic-bezier(.16,1,.3,1) forwards',
								}}
							>
								<div
									style={{
										display: 'flex',
										flexDirection: 'column',
										alignItems: 'center',
										textAlign: 'center',
										gap: 6,
										position: 'relative',
									}}
								>
									<div style={{ color: textPrimary, fontSize: isTiny ? 15 : isMobile ? 17 : 19, fontWeight: 600, letterSpacing: '-0.01em' }}>
										{t('notes.menu.title')}
									</div>
									<div style={{ color: textSecondary, fontSize: isTiny ? 11 : 13 }}>
										{String(selectedMenuNote?.nameNote || t('notes.untitled'))}
									</div>
									<button
										type="button"
										onClick={(event) => {
											event.stopPropagation();
											closeMenuAnimated();
										}}
										style={{
											position: 'absolute',
											top: 0,
											right: 0,
											background: `linear-gradient(180deg, ${hexToRgba(bgSecondary, 0.95)} 0%, ${hexToRgba(bgPrimary, 0.9)} 100%)`,
											border: `1px solid ${hexToRgba(textPrimary, 0.16)}`,
											borderRadius: 8,
											color: textSecondary,
											cursor: 'pointer',
											padding: isTiny ? 7 : 8,
											boxShadow: `0 6px 14px ${hexToRgba(bgPrimary, 0.3)}`,
											transition: 'transform 160ms ease, background-color 160ms ease, color 160ms ease, border-color 160ms ease, box-shadow 180ms ease',
										}}
										onMouseEnter={(event) => {
											event.currentTarget.style.transform = 'translateY(-1px)';
											event.currentTarget.style.color = textPrimary;
											event.currentTarget.style.borderColor = hexToRgba(activeAccent, 0.5);
											event.currentTarget.style.boxShadow = `0 10px 20px ${hexToRgba(bgPrimary, 0.4)}`;
										}}
										onMouseLeave={(event) => {
											event.currentTarget.style.transform = 'translateY(0)';
											event.currentTarget.style.color = textSecondary;
											event.currentTarget.style.borderColor = hexToRgba(textPrimary, 0.16);
											event.currentTarget.style.boxShadow = `0 6px 14px ${hexToRgba(bgPrimary, 0.3)}`;
										}}
										aria-label="Close menu"
									>
										<FiX size={16} />
									</button>
								</div>
								<div
									style={{
										display: 'grid',
										gridTemplateColumns: isTiny ? '1fr' : '1fr auto',
										gap: 12,
										alignItems: 'center',
										marginBottom: 4,
									}}
								>
									<input
										type="text"
										value={menuRenameDraft}
										autoFocus
										onClick={(event) => event.stopPropagation()}
										onPointerDown={(event) => event.stopPropagation()}
										onChange={(event) => setMenuRenameDraft(event.target.value)}
										onKeyDown={(event) => {
											if (event.key !== 'Enter') return;
											event.preventDefault();
											if (actionLoadingNoteId === String(selectedMenuNote?.noteId || '')) return;
											handleRenameNote(String(selectedMenuNote?.noteId || ''), menuRenameDraft);
										}}
										placeholder={t('notes.menu.newName')}
										style={{
											height: isTiny ? 42 : 50,
											padding: isTiny ? '0 12px' : '0 16px',
											borderRadius: 14,
											border: `1px solid ${hexToRgba(activeAccent, 0.38)}`,
											background: `linear-gradient(180deg, ${hexToRgba(bgPrimary, 0.9)} 0%, ${hexToRgba(bgSecondary, 0.95)} 100%)`,
											boxShadow: `inset 0 1px 0 ${hexToRgba(textPrimary, 0.08)}, 0 2px 8px ${hexToRgba(activeAccent, 0.12)}`,
											fontSize: isTiny ? 13 : 15,
											fontWeight: 500,
											color: textPrimary,
											outline: 'none',
											transition: 'box-shadow 180ms ease, border-color 180ms ease, transform 180ms ease',
										}}
										onFocus={(event) => {
											event.currentTarget.style.borderColor = hexToRgba(activeAccent, 0.68);
											event.currentTarget.style.boxShadow = `inset 0 1px 0 ${hexToRgba(textPrimary, 0.1)}, 0 0 0 2px ${hexToRgba(activeAccent, 0.22)}, 0 8px 20px ${hexToRgba(activeAccent, 0.12)}`;
										}}
										onBlur={(event) => {
											event.currentTarget.style.borderColor = hexToRgba(activeAccent, 0.38);
											event.currentTarget.style.boxShadow = `inset 0 1px 0 ${hexToRgba(textPrimary, 0.08)}, 0 2px 8px ${hexToRgba(activeAccent, 0.12)}`;
										}}
									/>
									<button
										type="button"
										disabled={
											actionLoadingNoteId === String(selectedMenuNote?.noteId || '') ||
											!String(menuRenameDraft || '').trim() ||
											String(menuRenameDraft || '').trim() === String(selectedMenuNote?.nameNote || '').trim()
										}
										onClick={(event) => {
											event.preventDefault();
											event.stopPropagation();
											handleRenameNote(String(selectedMenuNote?.noteId || ''), menuRenameDraft);
										}}
										style={{
											height: isTiny ? 42 : 50,
											padding: isTiny ? '0 14px' : '0 18px',
											borderRadius: 14,
											border: `1px solid ${hexToRgba(activeAccent, 0.5)}`,
											background: `linear-gradient(180deg, ${hexToRgba(activeAccent, 0.34)} 0%, ${hexToRgba(activeAccent, 0.2)} 100%)`,
											boxShadow: `0 6px 14px ${hexToRgba(activeAccent, 0.2)}`,
											color: textPrimary,
											fontWeight: 700,
											cursor: 'pointer',
											transition: 'transform 170ms ease, filter 170ms ease, box-shadow 180ms ease',
										}}
										onMouseEnter={(event) => {
											if (event.currentTarget.disabled) return;
											event.currentTarget.style.transform = 'translateY(-1px)';
											event.currentTarget.style.filter = 'brightness(1.06)';
											event.currentTarget.style.boxShadow = `0 12px 22px ${hexToRgba(activeAccent, 0.28)}`;
										}}
										onMouseLeave={(event) => {
											event.currentTarget.style.transform = 'translateY(0)';
											event.currentTarget.style.filter = 'brightness(1)';
											event.currentTarget.style.boxShadow = `0 6px 14px ${hexToRgba(activeAccent, 0.2)}`;
										}}
										>
										{t('notes.menu.ok')}
									</button>
								</div>
								<div
									style={{
										marginTop: 12,
										padding: isTiny ? '6px 0 0' : '8px 0 0',
										borderTop: `1px solid ${hexToRgba(textPrimary, 0.1)}`,
									}}
								>
								<button
									type="button"
									disabled={actionLoadingNoteId === String(selectedMenuNote?.noteId || '')}
									onClick={(event) => {
										event.preventDefault();
										event.stopPropagation();
										handleDeleteNote(String(selectedMenuNote?.noteId || ''));
									}}
									style={{
										width: '100%',
										textAlign: 'left',
										padding: isTiny ? '10px 12px' : '12px 14px',
										borderRadius: 12,
										border: 'none',
										background: `linear-gradient(180deg, ${hexToRgba('#C44242', 0.16)} 0%, ${hexToRgba('#C44242', 0.08)} 100%)`,
										color: '#C44242',
										cursor: actionLoadingNoteId === String(selectedMenuNote?.noteId || '') ? 'default' : 'pointer',
										boxShadow: `0 8px 18px ${hexToRgba('#C44242', 0.12)}`,
										transition: 'transform 170ms ease, filter 170ms ease, box-shadow 180ms ease',
									}}
									onMouseEnter={(event) => {
										if (event.currentTarget.disabled) return;
										event.currentTarget.style.transform = 'translateY(-1px)';
										event.currentTarget.style.filter = 'brightness(1.06)';
										event.currentTarget.style.boxShadow = `0 12px 24px ${hexToRgba('#C44242', 0.18)}`;
									}}
									onMouseLeave={(event) => {
										event.currentTarget.style.transform = 'translateY(0)';
										event.currentTarget.style.filter = 'brightness(1)';
										event.currentTarget.style.boxShadow = `0 8px 18px ${hexToRgba('#C44242', 0.12)}`;
									}}
									>
									Delete
								</button>
								</div>
								{menuStatusMessage ? (
									<div
										style={{
											marginTop: 10,
											padding: '10px 12px',
											borderRadius: 12,
											border: `1px solid ${hexToRgba(textPrimary, 0.12)}`,
											backgroundColor: hexToRgba(bgPrimary, 0.7),
											color: textSecondary,
											fontSize: isTiny ? 11 : 12,
											lineHeight: 1.4,
										}}
									>
										{menuStatusMessage}
									</div>
								) : null}
							</div>
						</div>,
						document.body
					)
					: null}
			</div>
		</div>
	);
}

export default MyNotes;
